import { useCreateUserMutation } from "@generated/graphql";
import { LoadingButton } from "@mui/lab";
import { Stack, Typography } from "@mui/material";
import AuthAgreementLabel from "components/auth/AuthAgreementLabel";
import AuthPasswordFields from "components/auth/AuthPasswordFields";
import { parseFullName, validateFullName } from "components/auth/utils";
import CheckboxControl from "components/form/CheckboxControl";
import Form from "components/form/Form";
import TextFieldControl from "components/form/TextFieldControl";
import ROUTES from "constants/routes";
import { ToastContext, ToastTypeEnum } from "context/toastContext";
import { authRedirect } from "helpers/cookieHelper";
import VALIDATION from "helpers/validation";
import { cleanSpaceInString } from "helpers/validation/createValidation";
import { useContext } from "react";
import { useForm } from "react-hook-form";

type UserRegisterForm = {
  fullName: string;
  email: string;
  password: string;
  confirmPassword: string;
  phoneNumber: string;
  agreement: boolean;
};

const FIELD_LABEL_PROPS = {
  variant: "body16rg" as const,
  sx: {
    color: "base.500",
    fontWeight: 600,
    mb: 0.5,
  },
};

const UserRegister = () => {
  const { addToast } = useContext(ToastContext);
  const form = useForm<UserRegisterForm>({
    defaultValues: {
      fullName: "",
      email: "",
      password: "",
      confirmPassword: "",
      phoneNumber: "",
      agreement: false,
    },
  });
  const { handleSubmit, watch } = form;
  const password = watch("password");
  const fullUserNameValidate = VALIDATION.fullUserName.validate;

  const [createUser, { loading }] = useCreateUserMutation();

  const onSubmit = (formData: UserRegisterForm) => {
    const parsedFullName = parseFullName(formData.fullName);

    createUser({
      variables: {
        input: {
          email: cleanSpaceInString(formData.email),
          password: formData.password,
          firstName: parsedFullName.firstName,
          lastName: parsedFullName.lastName,
          middleName: parsedFullName.middleName,
        },
      },
    })
      .then((res) => {
        const accessToken = res.data?.createUser.accessToken;
        const refreshToken = res.data?.createUser.refreshToken;

        if (!accessToken || !refreshToken) {
          addToast({ type: ToastTypeEnum.ERROR, text: "Произошла ошибка" });
          return;
        }

        authRedirect({
          accessToken,
          refreshToken,
          redirectUrl: `/${ROUTES.LOGIN}`,
        });
      })
      .catch((error) => {
        if (error?.message?.includes("Email должен быть уникальным")) {
          addToast({ type: ToastTypeEnum.ERROR, text: "Email должен быть уникальным" });
        } else {
          addToast({ type: ToastTypeEnum.ERROR, text: "Произошла ошибка" });
        }
      });
  };

  return (
    <Form
      form={form}
      onSubmit={handleSubmit(onSubmit)}>
      <Stack spacing={2.5}>
        <TextFieldControl
          label='ФИО'
          name='fullName'
          variant='outlined'
          labelTypographyProps={FIELD_LABEL_PROPS}
          placeholder='Иванова Анна Сергеевна'
          rules={{
            required: true,
            ...VALIDATION.fullUserName,
            validate: (value) => {
              const fullUserNameValidationResult = fullUserNameValidate(value);

              if (fullUserNameValidationResult !== true) {
                return fullUserNameValidationResult;
              }

              return validateFullName(value);
            },
          }}
        />

        <TextFieldControl
          label='Email'
          name='email'
          type='email'
          variant='outlined'
          labelTypographyProps={FIELD_LABEL_PROPS}
          placeholder='you@company.ru'
          rules={{
            required: true,
            ...VALIDATION.emailFull,
          }}
        />

        <AuthPasswordFields
          password={password}
          passwordLabel='Пароль'
          passwordPlaceholder='Минимум 8 символов'
          labelTypographyProps={FIELD_LABEL_PROPS}
        />

        <TextFieldControl
          label={
            <>
              Номер телефона{" "}
              <Typography
                component='span'
                variant='body16rg'
                sx={{ color: "text.disabled", fontWeight: 500 }}>
                - необязательно
              </Typography>
            </>
          }
          type='text'
          name='phoneNumber'
          variant='outlined'
          labelTypographyProps={FIELD_LABEL_PROPS}
          placeholder='+7 ___ ___-__-__'
          maskProps={{ mask: "+{7} (000) 000-00-00" }}
        />

        <CheckboxControl
          sx={{ "& .MuiLink-root": { color: "status.received" } }}
          label={<AuthAgreementLabel />}
          name='agreement'
          rules={{ validate: (value) => value || "Необходимо принять условия" }}
        />

        <LoadingButton
          type='submit'
          loading={loading}
          variant='contained'
          size='large'>
          Далее
        </LoadingButton>
      </Stack>
    </Form>
  );
};

export default UserRegister;
